import { InfiniteData } from '@tanstack/query-core';
import { useMutation } from '@tanstack/react-query';
import { Article, type ArticlesPaginatedList } from '@shared/types/articles';
import { ArticlesListParams, UpdateArticlePublicationDto } from '@src/api/articles.ts';
import { API } from '@src/api/index.ts';
import { queryClient } from '@src/api/query-client.ts';

export const useUpdateArticlePublicationMutation = () => {
  return useMutation({
    mutationFn: async (params: UpdateArticlePublicationDto) =>
      await API.articles.updatePublication(params),
    onSuccess: (response, { id }) => {
      queryClient.setQueriesData(
        { queryKey: ['articles'] },
        (oldData: InfiniteData<ArticlesPaginatedList>) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              results: page.results.map((item) =>
                item.id === id ? { ...item, publication: { ...response.data } } : item
              ),
            })),
          };
        }
      );

      queryClient.setQueriesData<InfiniteData<ArticlesPaginatedList>>(
        {
          queryKey: ['articles'],
          predicate: (query) => {
            const [, params] = query.queryKey as ['articles', ArticlesListParams];

            return params.tabName === 'all';
          },
        },
        (oldData) => {
          if (!oldData) return oldData;
          if (!response.data.is_published) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                results: [...page.results.filter((item) => item.id !== Number(id))],
              })),
            };
          } else {
            return oldData;
          }
        }
      );

      queryClient.setQueriesData({ queryKey: ['article', String(id)] }, (oldData: Article) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          publication: { ...oldData.publication, ...response.data },
        };
      });

      queryClient.invalidateQueries({ queryKey: ['article', String(id)] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
};
